import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import axios from "axios";
import { getCookie } from "../../utility/getCookie";
import { StoreLikeQueryDto, LikedStoresResponse, StoreLikeState } from "./types";

// Base URL for SellerStore Service
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const SELLER_STORE_API_URL = `${API_BASE_URL}/api/v1/seller`;

const api = axios.create({
    baseURL: SELLER_STORE_API_URL,
    withCredentials: true,
    headers: {
        "Content-Type": "application/json",
    },
});

// Request interceptor to add Bearer token from cookies
api.interceptors.request.use(
    async (config) => {
        const token = getCookie("access_token");
        if (token) {
            config.headers["Authorization"] = `Bearer ${token}`;
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

export const fetchLikedStores = createAsyncThunk(
    "storeLike/fetchLikedStores",
    async (query: StoreLikeQueryDto | undefined, { rejectWithValue }) => {
        try {
            const response = await api.get<LikedStoresResponse>("/store-like/customer", {
                params: query,
            });
            return response.data;
        } catch (error: any) {
            return rejectWithValue(
                error.response?.data?.message || "Failed to fetch liked stores"
            );
        }
    }
);

export const unlikeStore = createAsyncThunk(
    "storeLike/unlikeStore",
    async (targetStoreId: string, { rejectWithValue, dispatch }) => {
        try {
            const response = await api.post("/store-like/unlike", { targetStoreId });
            if (response.data.success) {
                dispatch(fetchLikedStores());
            }
            return response.data;
        } catch (error: any) {
            return rejectWithValue(
                error.response?.data?.message || "Failed to unlike store"
            );
        }
    }
);

const initialState: StoreLikeState = {
    likedStores: [],
    meta: null,
    loading: false,
    error: null,
    success: false,
    message: null,
};

const storeLikeSlice = createSlice({
    name: "storeLike",
    initialState,
    reducers: {
        clearLikeState: (state) => {
            state.loading = false;
            state.error = null;
            state.success = false;
            state.message = null;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchLikedStores.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchLikedStores.fulfilled, (state, action: PayloadAction<LikedStoresResponse>) => {
                state.loading = false;
                state.likedStores = action.payload.data;
                state.meta = action.payload.meta;
            })
            .addCase(fetchLikedStores.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            })
            .addCase(unlikeStore.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(unlikeStore.fulfilled, (state, action: PayloadAction<any>) => {
                state.loading = false;
                state.success = true;
                state.message = action.payload.message;
            })
            .addCase(unlikeStore.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            });
    },
});

export const { clearLikeState } = storeLikeSlice.actions;
export default storeLikeSlice.reducer;
